import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MyReturns, ReturnLine, ReturnRequestView } from '@miraki/contracts';
import { api, ApiError } from '../../api/client';
import { Alert, Button, Chip, Empty, Money, Note, PageHeader, Panel, Spinner, Table, Tiles } from '../../ui';
import { LINE_TONE, mayWithdraw } from './returns-view';
import { invalidateAll } from '../../api/write-keys';

/**
 * R-25 Return Requests — CN-13, CN-14 (D70, D71, D79).
 *
 * The retailer's requests, newest first, each with its pieces and what has happened to each one:
 * a decision is per piece (D71), so a request is never "approved" or "refused" as a whole and this
 * screen never says it is. A request is made from R-20 My Inventory, where the pieces are chosen.
 *
 * What comes back is BUDGET (CN-14), not money: nothing was owed for an unsold piece, so a
 * received piece returns its wholesale value to the budget and there is no credit note. The words
 * here say "returned to your budget", never "credited" or "refunded".
 *
 * Until a piece is received it is still HELD by the retailer and still in budget use, whatever its
 * line says — the screen says so, because "approved" is easily read as "gone".
 *
 * A request Miraki raised — a recall (CN-09) — is listed here too, labelled as Miraki's: it is the
 * same request, received the same way, and the retailer is the one who has to send the pieces.
 *
 * Floor: `shop.return_request`.
 */

function Amounts({ list, none }: { list: ReadonlyArray<{ currency: string; amount: string }>; none: string }) {
  if (list.length === 0) return <span className="mk-dim">{none}</span>;
  // Inline, one per currency and never added together (AN-08): this sits inside running text.
  return <>{list.map((a, i) => <span key={a.currency}>{i > 0 ? ' · ' : ''}<Money amount={a.amount} currency={a.currency} /></span>)}</>;
}

export function ShopReturns() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['shop', 'returns'], queryFn: () => api.get<MyReturns>('/shop/returns') });
  const [outcome, setOutcome] = useState<{ tone: 'them' | 'stop'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  const d = data!;

  const withdraw = async (r: ReturnRequestView) => {
    setOutcome(null);
    setBusy(r.id);
    try {
      await api.post(`/shop/returns/${r.id}/withdraw`);
      setOutcome({ tone: 'them', text: t('returns.withdrawn') });
      await invalidateAll(qc, [['shop']]);
    } catch (e) {
      const code = e instanceof ApiError ? (e.detail as { code?: string } | undefined)?.code : undefined;
      setOutcome({ tone: 'stop', text: code ? t(`returns.withdrawRefused.${code}`, { defaultValue: e instanceof Error ? e.message : '' }) : (e instanceof Error ? e.message : t('returns.failed')) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title={t('returns.title')}
        sub={t('returns.sub', { asOf: d.asOf })}
        actions={<Link to="/shop/inventory">{t('returns.fromInventory')}</Link>}
      />
      {outcome ? <Alert tone={outcome.tone}>{outcome.text}</Alert> : null}

      <Tiles items={[
        { k: t('returns.open'), v: d.requests.filter((r) => r.counts.REQUESTED + r.counts.APPROVED > 0).length, s: t('returns.openHint') },
        { k: t('returns.releasedToDate'), v: <Amounts list={d.releasedToDate} none={t('returns.nothingYet')} />, s: t('returns.releasedHint') },
      ]} />
      <Note>{t('returns.staysHeld')}</Note>

      {d.requests.length === 0 ? (
        <Panel pad>
          <Empty title={t('returns.empty')} body={t('returns.emptyBody')} />
        </Panel>
      ) : d.requests.map((r) => (
        <Panel
          key={r.id}
          pad
          // CN-09 — Miraki's recall is shown as Miraki's, with its reason, never as the retailer's own request.
          title={t(r.origin === 'MIRAKI' ? 'returns.recalledOn' : 'returns.requestedOn', { day: r.requestedAt.slice(0, 10) })}
          actions={mayWithdraw(r)
            ? <Button size="sm" tone="ghost" disabled={busy === r.id} onClick={() => void withdraw(r)}>{t('returns.withdraw')}</Button>
            : undefined}
        >
          {r.origin === 'MIRAKI' ? <Note>{t('returns.recallNote')}</Note> : null}
          <p>
            <b>{t('returns.remarks')}</b> {r.remarks}
          </p>
          <p className="mk-hint">
            {(Object.entries(r.counts) as Array<[ReturnLine['state'], number]>)
              .filter(([, n]) => n > 0)
              .map(([s, n]) => t(`returns.count.${s}`, { count: n }))
              .join(' · ')}
            {r.withdrawnAt ? <> · {t('returns.withdrawnOn', { day: r.withdrawnAt.slice(0, 10) })}</> : null}
          </p>
          <Table<ReturnLine>
            rows={[...r.lines]}
            rowKey={(l) => l.pieceId}
            columns={[
              { header: t('returns.colPiece'), render: (l) => <>{l.description}{l.note ? <div className="mk-hint">{l.note}</div> : null}</> },
              { header: t('returns.colWholesale'), numeric: true, render: (l) => <Money amount={l.wholesaleValue} currency={l.currency} /> },
              { header: t('returns.colDays'), numeric: true, render: (l) => (l.daysHeld ?? '—') },
              {
                header: t('returns.colState'),
                render: (l) => (
                  <>
                    <Chip tone={LINE_TONE[l.state]}>{t(`returns.state.${l.state}`)}</Chip>
                    {l.state === 'REFUSED' && l.refusalReason ? <div className="mk-hint">{l.refusalReason}</div> : null}
                    {l.state === 'RECEIVED' && l.receivedAt ? <div className="mk-hint">{t('returns.receivedOn', { day: l.receivedAt.slice(0, 10) })}</div> : null}
                  </>
                ),
              },
            ]}
          />
          <p className="mk-hint" style={{ textAlign: 'end' }}>
            {t('returns.returnedToBudget')} <Amounts list={r.released} none="—" />
            {r.awaiting.length ? <> · {t('returns.stillWithYou')} <Amounts list={r.awaiting} none="—" /></> : null}
          </p>
        </Panel>
      ))}
    </>
  );
}
